import {
  AssignmentDeviationEnum,
  AssignmentStatusEnum,
  AssignmentTypeEnum,
  AssignmentViewModel,
  CaseEventType,
  CaseStatusEnum,
  AssignmentViewModel as OrvalAssignmentViewModel,
} from 'api/model';
import Table from 'components/Table';
import {
  focusableTableCellClassName,
  tableCellClassName,
  TableColumnEnum,
} from 'components/Table/utils';
import { useAssignmentsFilters } from 'contexts/assignmentsFilters/useAssignmentsFilters';
import useTranslations from 'contexts/basicData/useTranslations';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import useQueuedAssignmentUpdates from './useQueuedAssignmentUpdates';
import { useAssignmentUpdateAssignmentStatus } from 'api/assignment/assignment';
import useCompanies from 'contexts/basicData/useCompanies';
import { arraySpreadIf } from 'utils/spreading';
import useAssignmentListColumnSettings, {
  ColumnProps as AssignmentListColumnProps,
} from './useAssignmentListColumnSettings';
import useAssignmentsBulkOperations from 'pages/GlobalSearch/useAssignmentsBulkOperations';
import useActiveColumns, {
  TableIdentifierEnum,
} from 'contexts/basicData/useActiveColumns';
import useAssignmentSearch from 'contexts/assignmentSearch/useAssignmentSearch';
import {
  adminHasUnreadCommentsClassName,
  assigneeIsUnavailableOnDate,
  assignmentStatusClassNames,
  assignmentStatusRowColorsCss,
  furtherTransportAssignmentClassName,
  getAssignmentStatusClassName,
  newDataFromRideClassName,
} from './AssignmentListStyles';

export const DotSpan = styled.span<{ dotColor?: string }>`
  height: 12px;
  width: 12px;
  background-color: ${({ dotColor }) =>
    dotColor ? `#${dotColor}` : 'transparent'};
  border-radius: 50%;
  display: inline-block;
  margin-right: 3px;
`;

const Center = styled.div`
  text-align: center;
`;

const makeStatusClassName = (assignment: AssignmentViewModel) => {
  if (assignment.case.caseStatusID === CaseStatusEnum.Pending) {
    return assignmentStatusClassNames.CasePending;
  }
  switch (assignment.assignment.assignmentDeviationID) {
    case AssignmentDeviationEnum.Aborted:
    case AssignmentDeviationEnum.Canceled:
      return assignmentStatusClassNames.Removed;
    default:
    // do nothigg;
  }
  return getAssignmentStatusClassName(assignment.assignment.assignmentStatusID);
};

const assignmentIdRowClassName = (assignmentId: number | string): string =>
  `assignment-row-${assignmentId}`;

const rowClassNameMaker = (row: AssignmentViewModel): string =>
  assignmentIdRowClassName(row.assignment.assignmentID);

export const coloredRowClassNameMaker = (row: AssignmentViewModel): string =>
  [
    assignmentIdRowClassName(row.assignment.assignmentID),
    makeStatusClassName(row),
    ...arraySpreadIf(row.case.newDataFromRide, newDataFromRideClassName),
    ...arraySpreadIf(
      !!row.assignment.assigneeIsUnavailableOnDate,
      assigneeIsUnavailableOnDate
    ),
    ...arraySpreadIf(
      row.case.adminHasUnreadComments,
      adminHasUnreadCommentsClassName
    ),
    ...arraySpreadIf(
      row.assignment.assignmentTypeID ===
        AssignmentTypeEnum.PlannedFurtherTransport,
      furtherTransportAssignmentClassName
    ),
  ].join(' ');

const TableStylerWrapper = styled.div<{ savingAssignmentIds: string[] }>`
  display: contents;

  ${({ savingAssignmentIds }) =>
    savingAssignmentIds.map(
      (id) => css`
        .${assignmentIdRowClassName(id)} {
          animation: assignmentlist-save-animation 1s infinite;
        }
      `
    )}

  @keyframes assignmentlist-save-animation {
    0% {
      background: #ddeefd;
    }
    50% {
      background: ${({ theme }) => theme.colors.background.primary};
    }
    100% {
      background: #ddeefd;
    }
  }
`;

const MyTable = styled(Table)`
  flex: 1;

  /* when a cell is focused, allow overflow for UserPicker */
  ${`.${tableCellClassName}`}:focus-within {
    overflow: visible;
  }

  ${`.${focusableTableCellClassName}`}:focus-within {
    padding: 0;
  }

  ${assignmentStatusRowColorsCss}
` as typeof Table;

const serverSortableColumns: Partial<Record<TableColumnEnum, number>> = {
  [TableColumnEnum.AssignmentBookedTo]: 0, // BookedTo
  [TableColumnEnum.AssignmentStatus]: 1, // Status
  [TableColumnEnum.Assignee]: 2, // Assignee
  [TableColumnEnum.CaseType]: 3, // CaseType
  [TableColumnEnum.AssignmentFromAddress]: 4, // FromAddress
};

export interface ServerSortDescriptor {
  orderByField: number;
  direction: number;
}

interface Props {
  onAssignmentItemClick?(model: AssignmentViewModel): void;
  onAssignmentUpdated(): Promise<any>;
  assignments: AssignmentViewModel[];
  bulkOperations?: ReturnType<typeof useAssignmentsBulkOperations>;
  loading?: boolean;
  showStatusColors: boolean;
  currentPage?: number;
  onGoToPage?(page: number): void;
  totalPageCount?: number;
  pageSize?: number;
  hideEstimatedDurationAndStartTime?: boolean;
  isDayPlanningView?: boolean;
  tableIdentifier?: TableIdentifierEnum;
  onServerSortChange?(sort: ServerSortDescriptor | null): void;
}

const AssignmentsList: React.FC<Props> = ({
  onAssignmentItemClick,
  onAssignmentUpdated,
  assignments,
  bulkOperations,
  loading,
  showStatusColors,
  currentPage,
  onGoToPage,
  totalPageCount,
  pageSize,
  hideEstimatedDurationAndStartTime,
  isDayPlanningView,
  tableIdentifier,
  onServerSortChange,
}) => {
  const translations = useTranslations();
  const { companies } = useCompanies();
  const [activeColumns] = useActiveColumns(tableIdentifier);
  const {
    state: { sortAscending, sortedColumnIndex },
    setSortState: setAssignmentFilterSortState,
    setDate,
  } = useAssignmentsFilters();
  const { setSortState: setAssignmentSearchSortState } = useAssignmentSearch();
  const { enqueueAssignmentUpdate, queues } = useQueuedAssignmentUpdates(
    assignments,
    onAssignmentUpdated
  );

  const updateStatusMutation = useAssignmentUpdateAssignmentStatus();

  const handleDirectStatusChange = useCallback(
    async (
      assignmentId: number,
      assignmentViewModel: AssignmentViewModel,
      newStatus: AssignmentStatusEnum,
      eventType: CaseEventType,
      eventMessage: string
    ) => {
      await updateStatusMutation.mutateAsync({
        // NSwag/Orval-bro: mutation-hooken förväntar Orval AssignmentViewModel.
        data: assignmentViewModel as unknown as OrvalAssignmentViewModel,
        params: {
          newStatus,
          eventType,
          eventMessage,
        },
      });
      await onAssignmentUpdated();
    },
    [updateStatusMutation, onAssignmentUpdated]
  );

  const columnSettings = useAssignmentListColumnSettings(
    hideEstimatedDurationAndStartTime,
    isDayPlanningView
  );

  const filteredColumnSettings = useMemo(() => {
    if (!tableIdentifier) return columnSettings;
    return columnSettings.filter((col) => {
      // show the column if it either has not yet been toggled off, or if it is not hidden by default
      if (
        col.identifier === undefined ||
        activeColumns[col.identifier] === true ||
        (activeColumns[col.identifier] === undefined &&
          col.hideByDefault !== true)
      )
        return true;
      return false;
    });
  }, [activeColumns, columnSettings, tableIdentifier]);

  const renderProps = useMemo(
    (): AssignmentListColumnProps => ({
      onSelectClick: (avm) => {
        bulkOperations?.toggleSelectAssignment(avm.assignment.assignmentID);
      },
      selectedAssignmentIds: bulkOperations?.selectedAssignmentIds ?? new Set(),
      translations,
      onUpdateAssignment: enqueueAssignmentUpdate,
      onDirectStatusChange: handleDirectStatusChange,
      companies: companies.reduce(
        (acc, curr) => {
          let companyAddress = `${curr.address}${curr.zip}${curr.city}`;
          companyAddress = companyAddress.toLowerCase().replaceAll(' ', '');
          acc[companyAddress] = curr;
          return acc;
        },
        {} as AssignmentListColumnProps['companies']
      ),
    }),
    [bulkOperations, companies, enqueueAssignmentUpdate, handleDirectStatusChange, translations]
  );

  const initialSortState = useRef({
    sortedColumnIndex,
    ascending: sortAscending,
  });

  const ongoingSaves = useMemo(() => Object.keys(queues), [queues]);

  useEffect(() => {
    setDate(new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TableStylerWrapper savingAssignmentIds={ongoingSaves}>
      {assignments.length === 0 ? (
        <Center>{loading ? 'Laddar' : 'Hittade inga uppdrag'}</Center>
      ) : (
        <MyTable
          columnSettings={filteredColumnSettings}
          currentPage={currentPage}
          fluid
          initialSortState={initialSortState.current}
          onGoToPage={onGoToPage}
          onRowClick={onAssignmentItemClick}
          onSortStateChange={(sortState) => {
            initialSortState.current = sortState;
            setAssignmentFilterSortState(sortState);
            setAssignmentSearchSortState(sortState);
            if (onServerSortChange) {
              const col = filteredColumnSettings[sortState.sortedColumnIndex];
              const sortField = col?.identifier
                ? serverSortableColumns[col.identifier]
                : undefined;
              if (sortField !== undefined) {
                onServerSortChange({
                  orderByField: sortField,
                  direction: sortState.ascending ? 0 : 1,
                });
              } else {
                onServerSortChange(null);
              }
            }
          }}
          pageSize={pageSize}
          renderProps={renderProps}
          rowClassName={
            showStatusColors ? coloredRowClassNameMaker : rowClassNameMaker
          }
          rows={assignments}
          totalPageCount={totalPageCount}
          useColumnWidthAsFlexAmount={
            // when device width > 768
            window.innerWidth > 768
          }
        />
      )}
    </TableStylerWrapper>
  );
};

export default AssignmentsList;
